import { DatePipe } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { Subscription } from 'rxjs'
import { CHART_COLOR_CODE } from 'src/app/features/shared/constants/chart-color-codes'
import { Footnote } from 'src/app/models/footnote.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { environment } from 'src/environments/environment'

import { Portfolio } from '../../portfolio.model'
import { PortfolioService } from '../../portfolio.service'

const MATURITY_TAB_LABEL = 'Distribution by maturity'
const ISSUER_TYPE = 'Issuer type'
const DISTRIBUTION_BY_CREDIT_QUALITY = 'Distribution by credit quality'
const DISTRIBUTION_BY_COUPON = 'Distribution by coupon'

const COMMODITY_ID = 'commodity'
const SECTORS_ID = 'sectors'
const REGIONS_ID = 'regions'
const MARKETS_ID = 'markets'
const ISSUER_TYPE_ID = 'issuer-type'
const DISTRIBUTION_BY_CREDIT_QUALITY_ID = 'distribution-by-credit-quality'
const MATURITY_ID = 'maturity'
const DISTRIBUTION_BY_COUPON_ID = 'distribution-by-coupon'

@Component({
  selector: 'exemplar-weighting-exposure',
  templateUrl: './weighting-exposure.component.html',
  styleUrls: ['./weighting-exposure.component.scss'],
  providers: [],
})
export class WeightingExposureComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('weightedExposuresTabs')
  weightedExposuresTabs: ElementRef<HTMLElement>
  @Input() portfolioModel: Portfolio
  @Input() sectorFootnote: Footnote
  @Input() creditQualityFootNote: Footnote
  public activeTab: number
  public datePipe: DatePipe
  setViewportWidth: number

  //AEM content variables
  distributionEffectiveMaturity: AEMChunk
  distributionStateMaturity: AEMChunk
  disclaimerWeightedExposures: AEMChunk
  aemContentSub: Subscription

  //chart
  chartId = 'assetAllocationPieChart'
  tableId = 'assetAllocationTable'

  //table
  dataSource: MatTableDataSource<any>
  displayedColumns = ['conByStateColor', 'name', 'value', 'benchmark', 'weight']
  displayedTabs = []
  displayedTabsMobile = []
  mobileTabIndex: string
  chartAndTable = []
  showSectorsTab = false
  sectorData = []
  regionData = []
  marketData = []
  isCommodityFund = false
  issuerTypeData = []
  creditQualityData = []
  maturityData = []
  couponData = []
  selectPathData = undefined
  donutChartContent = {}
  activeTabName = 'Sectors'
  colorIndex = 0
  tickerColName = ''
  weightingsAsOfDate = ''
  benchmarkShortName = ''
  weightingData: any
  isInternationalFund = false
  isGlobalFund = false
  isTaxExemptBondFund: boolean
  isTaxExemptMoneyMarketFund: boolean
  isMoneyMarket: boolean
  isBond: boolean
  isBalanced: boolean
  isFundOfFunds: boolean
  showIssuerTypeTab = false
  showCreditQualityTab = false
  showMaturityTab = false
  showCouponTab = false
  dialogTitle: string

  tableContainer: HTMLElement
  scrollBarPosition = 0
  showRightButton = true
  innerWidth: number
  defaultTab: string
  nonVanguardFundsViewableFlag: boolean

  /* eslint max-params: [2, 5] */
  constructor(
    private readonly portfolioService: PortfolioService,
    private readonly renderer: Renderer2,
    public viewContainer: ViewContainerRef,
    private readonly centralContentService: CentralContentService,
  ) {
    this.datePipe = new DatePipe('en-US')
  }
  ngOnInit() {
    const model = this.portfolioModel
    this.initFlags(model)
    this.tabData(model)
    this.initTabsData(model)
    this.defaultTab = this.getDefaultSelectedTab()
    this.showIssuerTypeTab = true
    this.showCreditQualityTab = true
    this.showMaturityTab = true
    this.showCouponTab = true
    this.showSectorsTab = this.sectorData ? true : false
    this.onTabClick(this.defaultTab)
    this.findScreenSize()
    this.nonVanguardFundsViewableFlag = environment.nonVanguardFundsViewableFlag
    if (
      (!this.portfolioModel?.profileData?.isExternalFund &&
        this.defaultTab === 'Sectors') ||
      this.defaultTab === 'Commodity'
    ) {
      this.tableHandler()
    } else {
      this.showRightButton = false
    }
    this.prepareMobileTabset()

    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    this.distributionEffectiveMaturity =
      this.centralContentService.getContentByBody(
        'fund_definitions_distribution_by_effective_maturity',
        content,
      )
    this.distributionStateMaturity =
      this.centralContentService.getContentByBody(
        'fund_definitions_distribution_by_state_maturity',
        content,
      )
    this.disclaimerWeightedExposures =
      this.centralContentService.getContentByBody(
        'disclaimer_weighted_exposures_chart',
        content,
      )
  }

  ngAfterViewInit() {
    const tabs =
      this.weightedExposuresTabs?.nativeElement.querySelectorAll(
        "button[role='tab']",
      )
    for (let i = 0; i < tabs?.length; i++) {
      this.renderer.setAttribute(
        tabs[i],
        'ARIA_CONTROLS',
        'weighted-exposures-tabpanel',
      )
    }
  }

  tableHandler() {
    setTimeout(() => {
      this.tableContainer = document.querySelector(
        '#assetAllocationTableExportDiv',
      )
      this.tableContainer?.addEventListener('scroll', () => {
        this.scrollTableHandler()
      })
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 300)
  }

  tabData(model) {
    this.displayedTabs = [
      {
        id: 1,
        name: this.isCommodityFund ? 'Commodity Sector Breakdown' : 'Sectors',
        tabClass: 'Sectors',
        tabPanelId: this.isCommodityFund ? COMMODITY_ID : SECTORS_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 2,
        name: 'Regions',
        tabPanelId: REGIONS_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 3,
        name: 'Markets',
        tabPanelId: MARKETS_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 4,
        name: ISSUER_TYPE,
        tabClass: 'Issuer',
        tabPanelId: ISSUER_TYPE_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 5,
        name: DISTRIBUTION_BY_CREDIT_QUALITY,
        tabClass: 'Credit rating',
        tabPanelId: DISTRIBUTION_BY_CREDIT_QUALITY_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 6,
        name: MATURITY_TAB_LABEL,
        tabClass: 'Maturity',
        tabPanelId: MATURITY_ID,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        id: 7,
        name: DISTRIBUTION_BY_COUPON,
        tabClass: 'Coupon',
        tabPanelId: DISTRIBUTION_BY_COUPON_ID,
      },
    ]
  }

  initFlags(model: Portfolio): void {
    this.tickerColName = model.profileData.ticker
    this.isInternationalFund = model.profileData.isInternational
    this.isGlobalFund = model.profileData.isGlobal
    this.isCommodityFund = this.tickerColName === 'VCMDX'
    this.isMoneyMarket = model.profileData.isMoneyMarket
    this.isTaxExemptBondFund = model.profileData.isTaxExemptBondFund
    this.isTaxExemptMoneyMarketFund =
      model.profileData.isTaxExemptMoneyMarketFund
    this.isBalanced = model.profileData.isBalanced
    this.isBond = model.profileData.isBond
    this.isFundOfFunds = model.profileData.isFundOfFunds
  }

  initTabsData(model: Portfolio): void {
    this.weightingData = this.getWeightingConfig(model)
    this.sectorData = this.getSectorData(model)
    this.regionData = this.getRegionData(model)
    this.marketData = this.getMarketData(model.weighting.country)
    this.creditQualityData = this.getCreditQualityData(model)
    this.issuerTypeData = this.getIssuerData(model)
    this.maturityData = this.getMaturityData(model)
    this.couponData = this.getCouponData(model)
  }

  // eslint-disable-next-line complexity
  getDefaultSelectedTab(): string {
    if (this.sectorData) {
      return this.isCommodityFund ? COMMODITY_ID : SECTORS_ID
    }

    if (this.isInternationalFund || (this.isGlobalFund && !this.sectorData)) {
      return REGIONS_ID
    }

    if (this.marketData) {
      return MARKETS_ID
    }

    if (this.creditQualityData) {
      return DISTRIBUTION_BY_CREDIT_QUALITY_ID
    }

    if (this.issuerTypeData) {
      return ISSUER_TYPE_ID
    }

    if (this.maturityData) {
      return MATURITY_ID
    }

    return DISTRIBUTION_BY_COUPON_ID
  }

  getWeightingConfig(model: Portfolio): any {
    return {
      Sectors: {
        asOfDate: model.weighting?.sector?.currentAsOfDate,
        shortName: model.weighting?.sector?.benchmarkShortName,
      },
      Regions: {
        asOfDate: model.allocation?.region?.asOfDate,
        shortName: model.weighting?.sector?.benchmarkShortName,
      },
      Markets: {
        asOfDate: model.weighting?.country?.currentAsOfDate,
      },
      Commodity: {
        asOfDate: model.weighting?.sector?.currentAsOfDate,
        shortName: model.weighting?.sector?.benchmarkShortName,
      },
      'Credit rating': {
        asOfDate: model.classification?.creditQuality?.asOfDate,
      },
      Issuer: {
        asOfDate: model.classification?.issuer?.asOfDate,
      },
      Maturity: {
        asOfDate: model.classification?.maturity?.asOfDate,
      },
      Coupon: {
        asOfDate: model.classification?.coupon?.asOfDate,
      },
    }
  }

  getSectorData(model: Portfolio): any {
    const filteredSectorData = model.weighting?.sector?.long[0]?.item?.filter(
      (sector) => sector?.currYrPct !== '0.00',
    )
    if (filteredSectorData) {
      return filteredSectorData.map((longItem: any, index) => {
        const benchmarkValue = this.portfolioModel?.profileData
          ?.isMarketNeutralFund
          ? model.weighting?.sector?.short[0].item[index].currYrPct
          : longItem.bnkCurrYrPct
        return {
          id: longItem.name + index,
          name: longItem.name,
          value: longItem.currYrPct,
          benchmark: benchmarkValue,
          weight: longItem.currYrPct - longItem.bnkCurrYrPct,
          color: this.getColorCode(index),
        }
      })
    }
  }

  getRegionData(model: Portfolio): any {
    const filteredRegionData = model.allocation?.region?.region?.filter(
      (allocation) => !!allocation.percent && allocation.percent !== '0.0',
    )
    return filteredRegionData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.percent,
        benchmark: '-',
        weight: longItem.percent,
        color: this.getColorCode(index),
      }
    })
  }

  getMarketData(countryData: any): any {
    const filteredCountryData = countryData?.item?.filter(
      (country) => !!country.currYrPct && country.currYrPct !== '0.0',
    )
    return filteredCountryData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.currYrPct,
        color: this.getColorCode(index),
      }
    })
  }

  getCommodityData() {
    this.chartAndTable = [...this.sectorData]
    this.activeTabName = 'Sectors'
    this.showSectorsTab = true
    this.displayedColumns = ['conByStateColor', 'name', 'value', 'benchmark']
  }

  getCreditQualityData(model: Portfolio): any {
    const filteredCreditData =
      model.classification?.creditQuality?.item?.filter(
        (creditQuality) =>
          !!creditQuality.marketValuePct &&
          creditQuality.marketValuePct !== '0.0',
      )
    return filteredCreditData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.marketValuePct,
        color: this.getColorCode(index),
      }
    })
  }

  getIssuerData(model: Portfolio): any {
    const filteredIssuerData = model.classification?.issuer?.item?.filter(
      (issuer) => !!issuer.marketValuePct && issuer.marketValuePct !== '0.0',
    )
    return filteredIssuerData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.marketValuePct,
        color: this.getColorCode(index),
      }
    })
  }

  getMaturityData(model: Portfolio): any {
    const filteredMaturityData = model.classification?.maturity?.item?.filter(
      (maturity) =>
        !!maturity.marketValuePct && maturity.marketValuePct !== '0.0',
    )
    return filteredMaturityData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.marketValuePct,
        color: this.getColorCode(index),
      }
    })
  }

  getCouponData(model: Portfolio): any {
    const filteredCouponData = model.classification?.coupon?.item?.filter(
      (coupon) => !!coupon.marketValuePct && coupon.marketValuePct !== '0.0',
    )
    return filteredCouponData?.map((longItem: any, index) => {
      return {
        id: longItem.name + index,
        name: longItem.name,
        value: longItem.marketValuePct,
        color: this.getColorCode(index),
      }
    })
  }

  /*eslint max-statements: ["error", 21, { "ignoreTopLevelFunctions": true }]*/
  //eslint-disable-next-line complexity
  onTabClick(event: string): void {
    this.inItSelectedTabConfig()
    this.activeTabName = this.setActiveTabNameFromEvent(event)
    if (event === REGIONS_ID) {
      this.chartAndTable = [...this.regionData]
      this.displayedColumns = ['conByStateColor', 'name', 'value']
    } else if (event === SECTORS_ID) {
      this.chartAndTable = [...this.sectorData]
    } else if (event === MARKETS_ID) {
      this.chartAndTable = [...this.marketData]
      this.activeTabName = 'Markets'
      this.displayedColumns = ['conByStateColor', 'name', 'value']
    } else if (event === COMMODITY_ID) {
      this.getCommodityData()
    } else if (event === ISSUER_TYPE_ID) {
      this.chartAndTable = [...this.issuerTypeData]
      this.activeTabName = 'Issuer'
      this.displayedColumns = ['conByStateColor', 'name', 'value']
    } else if (event === DISTRIBUTION_BY_CREDIT_QUALITY_ID) {
      this.getCreditData()
    } else if (event === MATURITY_ID) {
      this.getMaturityTypeData()
    } else if (event === DISTRIBUTION_BY_COUPON_ID && this.couponData) {
      this.chartAndTable = [...this.couponData]
      this.activeTabName = 'Coupon'
      this.displayedColumns = ['conByStateColor', 'name', 'value']
    }
    this.dataSource = new MatTableDataSource(this.chartAndTable)

    if (
      !(this.activeTabName === 'Sectors' || this.activeTabName === 'Commodity')
    ) {
      this.showRightButton = false
    }
  }

  setActiveTabNameFromEvent(event: string): string {
    const eventToTabName = {
      [SECTORS_ID]: 'Sectors',
      [COMMODITY_ID]: 'Commodity',
      [DISTRIBUTION_BY_COUPON_ID]: 'Coupon',
      [MARKETS_ID]: 'Markets',
      [DISTRIBUTION_BY_CREDIT_QUALITY_ID]: 'Credit rating',
      [ISSUER_TYPE_ID]: 'Issuer',
      [REGIONS_ID]: 'Regions',
      [MATURITY_ID]: 'Maturity',
    }

    return eventToTabName[event]
  }

  inItSelectedTabConfig() {
    this.chartAndTable = []
    this.displayedColumns = [
      'conByStateColor',
      'name',
      'value',
      'benchmark',
      'weight',
    ]
    this.dataSource = new MatTableDataSource(this.chartAndTable)
  }

  getCreditData() {
    this.chartAndTable = [...this.creditQualityData]
    this.activeTabName = 'Credit rating'
    this.displayedColumns = ['conByStateColor', 'name', 'value']
  }

  getMaturityTypeData() {
    this.chartAndTable = [...this.maturityData]
    this.activeTabName = 'Maturity'
    this.displayedColumns = ['conByStateColor', 'name', 'value']
  }

  getColorCode(index) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    let colorIndex = index
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (index > 15) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const str = index.toString()
      colorIndex = str.charAt(str.length - 1)
    }
    this.colorIndex = parseInt(colorIndex)
    return CHART_COLOR_CODE.color[this.colorIndex]
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public setDialogInfo(title: string) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.dialogTitle = 'Effective maturity vs. stated maturity'
  }

  selectPath(rowId) {
    this.selectPathData = {
      chartId: this.chartId,
      tableId: this.tableId,
      trId: rowId,
    }
  }

  findScreenSize() {
    this.innerWidth = window.innerWidth
  }

  scrollTableHandler() {
    this.showRightButton =
      this.tableContainer.offsetWidth + this.tableContainer.scrollLeft >
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      this.tableContainer.scrollWidth - 1
        ? false
        : true
    this.scrollBarPosition = this.tableContainer.scrollLeft
  }

  moveRight() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.tableContainer.scrollLeft += 200
  }

  moveLeft() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.tableContainer.scrollLeft -= 200
  }

  prepareMobileTabset() {
    this.displayedTabsMobile = this.displayedTabs
    // eslint-disable-next-line complexity
    this.displayedTabsMobile = this.displayedTabsMobile.filter((tabs) => {
      switch (true) {
        case (tabs.name === 'Sectors' ||
          tabs.name === 'Commodity Sector Breakdown') &&
          this.showSectorsTab: {
          return true
        }
        case tabs.name === 'Regions' && !!this.regionData?.length: {
          return true
        }
        case tabs.name === 'Markets' && !!this.marketData?.length: {
          return true
        }
        case tabs.name === DISTRIBUTION_BY_CREDIT_QUALITY &&
          !!this.creditQualityData?.length &&
          this.showCreditQualityTab: {
          return true
        }
        case tabs.name === ISSUER_TYPE &&
          !!this.issuerTypeData?.length &&
          this.showIssuerTypeTab: {
          return true
        }
        case tabs.name === MATURITY_TAB_LABEL &&
          !!this.maturityData?.length &&
          this.showMaturityTab: {
          return true
        }
        case tabs.name === DISTRIBUTION_BY_COUPON &&
          !!this.couponData?.length &&
          this.showCouponTab: {
          return true
        }
        default: {
          return false
        }
      }
    })
  }

  tableHasThreeColumns(): boolean {
    const expectedColumns = ['conByStateColor', 'name', 'value']

    if (this.displayedColumns.length !== expectedColumns.length) {
      return false
    }

    for (let i = 0; i < this.displayedColumns.length; i++) {
      if (this.displayedColumns[i] !== expectedColumns[i]) {
        return false
      }
    }

    return true
  }

  changeView(): number {
    if (this.displayedTabsMobile.length < 2) {
      this.setViewportWidth = 767
    } else {
      this.setViewportWidth = 375
    }
    return this.setViewportWidth
  }

  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
